// Impresión en un iframe con srcdoc.
//
// La alternativa obvia —tapar la página con CSS y limpiar en `afterprint`— no
// sirve: en Android `print()` no bloquea, el evento llega antes de tiempo y
// termina saliendo la página entera (AGENTS.md sección 8).

/** Saca lo que ningún sistema de archivos acepta y recorta a algo manejable. */
export function nombreDeArchivo(texto: string): string {
  return texto
    .replace(/[/\\:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 90);
}

/**
 * Se lee una sola vez, al cargar. No sirve leerlo dentro de `imprimirHtml`: si
 * se imprimen dos presupuestos con menos de un minuto de diferencia, el segundo
 * guardaría como "previo" el título pisado por el primero y la pestaña quedaría
 * llamándose "Presupuesto 1 - ..." para siempre.
 */
const TITULO_DE_LA_APP = document.title;

/**
 * `nombre` termina siendo el nombre del PDF: al imprimir un iframe el navegador
 * no mira el `<title>` de la hoja, mira el `document.title` de la pestaña. Por
 * eso se pisa y se restaura después.
 *
 * Se restaura con el mismo timeout que saca el iframe y no en `afterprint`: en
 * Android ese evento llega antes de que el usuario haya elegido dónde guardar,
 * y restaurarlo ahí devuelve el nombre viejo justo a tiempo para arruinarlo.
 * El costo es que la pestaña queda con otro título un rato. No molesta a nadie.
 */
export function imprimirHtml(html: string, nombre?: string): void {
  if (nombre) document.title = nombreDeArchivo(nombre) || TITULO_DE_LA_APP;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  iframe.srcdoc = html;

  iframe.addEventListener("load", () => {
    const v = iframe.contentWindow;
    if (!v) return;
    v.focus();
    v.print();
    // No se saca en afterprint por lo de Android: se deja un rato y listo.
    setTimeout(() => {
      iframe.remove();
      document.title = TITULO_DE_LA_APP;
    }, 60000);
  });

  document.body.appendChild(iframe);
}

/**
 * El esqueleto del papel: HTML autocontenido con el CSS de impresión que
 * comparten el presupuesto y el contrato. Cada documento arma su cuerpo;
 * acá vive todo lo que tiene que verse igual en los dos.
 */
export function documentoImpreso(titulo: string, cuerpo: string): string {
  return `<!doctype html>
<html lang="es"><head><meta charset="UTF-8" />
<title>${titulo}</title>
<style>
  @page { margin: 18mm; }
  body { font: 12pt/1.45 system-ui, sans-serif; color: #111; background: #fff; margin: 0; }
  h1 { font-size: 18pt; margin: 0 0 2mm; }
  .gris { color: #555; }
  .datos { margin: 6mm 0; }
  .datos div { margin-bottom: 1mm; }
  table { width: 100%; border-collapse: collapse; margin-top: 4mm; }
  th { text-align: left; font-size: 9pt; text-transform: uppercase; letter-spacing: .04em;
       color: #555; border-bottom: 1px solid #999; padding: 2mm 1mm; }
  td { padding: 2mm 1mm; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
  .num { text-align: right; white-space: nowrap; }
  .total { margin-top: 4mm; text-align: right; font-size: 14pt; font-weight: 700; }
  .notas { margin-top: 8mm; white-space: pre-wrap; }
  .parrafo { margin-top: 4mm; white-space: pre-wrap; }
  .condiciones { margin-top: 10mm; page-break-inside: avoid; }
  .condiciones h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: .04em;
                    border-bottom: 1px solid #999; padding-bottom: 1.5mm; }
  .clausula { margin-top: 3mm; font-size: 10.5pt; }
  .clausula div { color: #333; }
  .firmas { margin-top: 18mm; display: flex; gap: 14mm; page-break-inside: avoid; }
  .firma { flex: 1; border-top: 1px solid #111; padding-top: 2mm; font-size: 10pt;
           color: #333; }
  footer { margin-top: 10mm; font-size: 9pt; color: #555; border-top: 1px solid #e0e0e0;
           padding-top: 3mm; }
</style></head>
<body>${cuerpo}</body></html>`;
}
